// ========================================
// pages/Account.js - User Account Management
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 12, 2026
// 
// PURPOSE: Allow users to manage their account profile
// 
// FEATURES:
// - View profile information (semi-hidden sensitive data)
// - Update full name
// - Change password
// - Link to verification settings
// - Logout
// 
// NOTE: Email and phone changes are disabled for now (coming soon)
// 
// FOOTER FIX: Footer is full width, no left/right margins
// ========================================

import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import Footer from '../components/Footer';

const Account = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form states
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updating, setUpdating] = useState(false);
  
  // Mask email for semi-anonymity
  const maskEmail = (email) => {
    if (!email) return '';
    const [name, domain] = email.split('@');
    if (name.length <= 3) return email;
    return `${name.slice(0, 3)}***@${domain}`;
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUser(response.data.user);
      setFullName(response.data.user.fullName || '');
      setPhoneNumber(response.data.user.phoneNumber || '');
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load user data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/profile`, 
        { fullName, phoneNumber },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setSuccess('Profile updated successfully');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setUpdating(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${process.env.REACT_APP_API_URL}/api/users/change-password`,
        { currentPassword, newPassword },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password changed successfully');
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F5F0E6', display: 'flex', flexDirection: 'column' }}>
      
      {/* Main Content Area with Padding */}
      <div style={{ padding: '20px', flex: 1 }}>
        
        {/* Header */}
        <div className="account-header">
          <Link to="/dashboard" className="back-link">← Back to Dashboard</Link>
          <h1 className="account-title">My Account</h1>
          <div className="account-placeholder"></div>
        </div>
        
        <div className="account-container">
          
          {/* Profile Information */}
          <div className="account-card">
            <h2 className="account-card-title">👤 Profile Information</h2>
            <form onSubmit={handleUpdateProfile} className="account-form">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your full name"
                  className="account-input"
                />
              </div>
              
              <div className="form-group">
                <label>Email Address</label>
                <div className="semi-hidden-field">
                  <span>{maskEmail(user?.email)}</span>
                  <span className="semi-hidden-badge">🔒 Semi-anonymous</span>
                </div>
                <small className="field-note">Email cannot be changed. Contact support for assistance.</small>
              </div>
              
              <div className="form-group">
                <label>Phone Number</label>
                <div className="phone-field">
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0244XXXXXX"
                    className="account-input"
                  />
                  <span className="phone-note">(Verification required to change - coming soon)</span>
                </div>
              </div>
              
              <button type="submit" className="account-btn primary" disabled={updating}>
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
          
          {/* Change Password */}
          <div className="account-card">
            <h2 className="account-card-title">🔐 Change Password</h2>
            <form onSubmit={handleChangePassword} className="account-form">
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="account-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="account-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="account-input"
                  required
                />
              </div>
              
              <button type="submit" className="account-btn primary" disabled={updating}>
                {updating ? 'Updating...' : 'Change Password'}
              </button>
            </form>
          </div>
          
          {/* Quick Actions */}
          <div className="account-card">
            <h2 className="account-card-title">⚡ Quick Actions</h2>
            
            <Link to="/settings" className="quick-action-link">
              <span>⚙️</span>
              <div>
                <strong>Verification Settings</strong>
                <small>Configure death verification thresholds</small>
              </div>
              <span className="action-arrow">→</span>
            </Link>
            
            <button onClick={handleLogout} className="quick-action-logout">
              <span>🚪</span>
              <div>
                <strong>Logout</strong>
                <small>Sign out of your account</small>
              </div>
              <span className="action-arrow">→</span>
            </button>
          </div>
          
          {/* Danger Zone - Account Deletion (Disabled for now) */}
          <div className="account-card danger-zone">
            <h2 className="account-card-title">⚠️ Danger Zone</h2>
            <p className="danger-warning">
              Account deletion is currently disabled. Contact support if you need to delete your account.
            </p>
          </div>
          
          {/* Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
        </div>
      </div>
      
      {/* Footer - Full Width */}
      <Footer />
      
      <style dangerouslySetInnerHTML={{ __html: `
        .account-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 800px;
          margin: 0 auto 30px;
          width: 100%;
          flex-wrap: wrap;
          gap: 15px;
        }
        
        .back-link {
          color: #2E7D32;
          text-decoration: none;
          font-size: 14px;
        }
        
        .account-title {
          font-size: 28px;
          color: #1A1A1A;
          margin: 0;
        }
        
        .account-placeholder {
          width: 100px;
        }
        
        .account-container {
          max-width: 800px;
          margin: 0 auto 30px;
          width: 100%;
        }
        
        .account-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 25px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .account-card-title {
          font-size: 20px;
          color: #2E7D32;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #FFD700;
          display: inline-block;
        }
        
        .account-form {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }
        
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .form-group label {
          font-size: 14px;
          font-weight: bold;
          color: #1A1A1A;
        }
        
        .account-input {
          padding: 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          width: 100%;
          box-sizing: border-box;
        }
        
        .account-input:focus {
          outline: none;
          border-color: #FFD700;
        }
        
        .semi-hidden-field {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          padding: 12px;
          background: #F5F0E6;
          border-radius: 8px;
        }
        
        .semi-hidden-badge {
          font-size: 12px;
          background: #FFD700;
          padding: 4px 10px;
          border-radius: 20px;
          color: #1A1A1A;
        }
        
        .field-note {
          font-size: 11px;
          color: #999;
          margin-top: 4px;
        }
        
        .phone-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        
        .phone-note {
          font-size: 11px;
          color: #FF9800;
        }
        
        .account-btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.3s;
        }
        
        .account-btn.primary {
          background: #FFD700;
          color: #1A1A1A;
        }
        
        .account-btn.primary:hover {
          background: #B8860B;
        }
        
        .quick-action-link {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #F5F0E6;
          border-radius: 10px;
          text-decoration: none;
          margin-bottom: 12px;
          transition: all 0.3s;
        }
        
        .quick-action-link:hover {
          background: #E8E0D0;
          transform: translateX(5px);
        }
        
        .quick-action-logout {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #F5F0E6;
          border-radius: 10px;
          border: none;
          width: 100%;
          cursor: pointer;
          text-align: left;
          transition: all 0.3s;
        }
        
        .quick-action-logout:hover {
          background: #fee2e2;
          transform: translateX(5px);
        }
        
        .quick-action-link span:first-child,
        .quick-action-logout span:first-child {
          font-size: 24px;
        }
        
        .quick-action-link div,
        .quick-action-logout div {
          flex: 1;
        }
        
        .quick-action-link strong,
        .quick-action-logout strong {
          display: block;
          font-size: 14px;
          color: #1A1A1A;
        }
        
        .quick-action-link small,
        .quick-action-logout small {
          font-size: 12px;
          color: #666;
        }
        
        .action-arrow {
          font-size: 18px;
          color: #FFD700;
        }
        
        .danger-zone {
          border: 1px solid #dc2626;
          background: #fff5f5;
        }
        
        .danger-warning {
          color: #dc2626;
          font-size: 14px;
        }
        
        .error-message {
          background: #fee2e2;
          border: 1px solid #ef4444;
          color: #dc2626;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .success-message {
          background: #d1fae5;
          border: 1px solid #10b981;
          color: #065f46;
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #F5F0E6;
          border-top: 4px solid #FFD700;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .account-header {
            flex-direction: column;
            text-align: center;
          }
          
          .account-title {
            font-size: 24px;
          }
          
          .account-placeholder {
            display: none;
          }
          
          .account-card {
            padding: 20px;
          }
          
          .account-btn {
            width: 100%;
            text-align: center;
          }
        }
      `}} />
    </div>
  );
};

export default Account;